import { FC, useMemo } from 'react';
import styled from '@emotion/styled';

import { ScheduleAvailType } from '@/redux/slices/slice_types';
import AddAvailList from '../NewScheduleAvails/AddAvailList';
import IncrementalSelectedAvailList from './AddIncrementalLaunch/IncrementalSelectedAvailList';

export type IncrementalSelectionState = {
  add: string[];
  remove: string[];
};

type IncrementalLaunchRightsSelectionSectionProps = {
  tab: ScheduleAvailType;
  value: IncrementalSelectionState;
  //TODO: originalRights here is actually originalSelections, we should update that to prevent confusion
  originalRights: string[];
  rightsOptions?: string[];
  onChange: (next: IncrementalSelectionState) => void;
  scrollToElementName?: string;
  mode: 'add' | 'remove';
};

const getDisplayedRights = ({ originalRights, state }: { originalRights: string[]; state: IncrementalSelectionState }): string[] => {
  const seen = new Set<string>();
  const displayedRights: string[] = [];

  state.add.forEach((right) => {
    if (seen.has(right)) return;
    seen.add(right);
    displayedRights.push(right);
  });

  originalRights.forEach((right) => {
    if (seen.has(right)) return;
    seen.add(right);
    displayedRights.push(right);
  });

  return displayedRights;
};

const IncrementalLaunchRightsSelectionSection: FC<IncrementalLaunchRightsSelectionSectionProps> = ({
  tab,
  value,
  originalRights,
  rightsOptions,
  onChange,
  scrollToElementName,
  mode
}) => {
  const displayedRights = useMemo(() => getDisplayedRights({ originalRights, state: value }), [originalRights, value]);

  const handleAddRight = (right: string) => {
    if (value.remove.includes(right)) {
      onChange({
        ...value,
        remove: value.remove.filter((item) => item !== right)
      });
      return;
    }

    if (originalRights.includes(right) || value.add.includes(right)) {
      return;
    }

    onChange({
      ...value,
      add: [...value.add, right]
    });
  };

  const handleAddMultipleRights = (rights: string[]) => {
    if (!rights.length) return;

    const nextAdd = [...value.add];
    let nextRemove = [...value.remove];
    let hasChanges = false;

    rights.forEach((right) => {
      if (nextRemove.includes(right)) {
        nextRemove = nextRemove.filter((item) => item !== right);
        hasChanges = true;
        return;
      }

      if (originalRights.includes(right) || nextAdd.includes(right)) {
        return;
      }

      nextAdd.push(right);
      hasChanges = true;
    });

    if (!hasChanges) return;

    onChange({
      ...value,
      add: nextAdd,
      remove: nextRemove
    });
  };

  const handleToggleRight = (right: string) => {
    if (value.add.includes(right)) {
      onChange({
        ...value,
        add: value.add.filter((item) => item !== right)
      });
      return;
    }

    if (!originalRights.includes(right)) {
      return;
    }

    const isRemoved = value.remove.includes(right);

    onChange({
      ...value,
      remove: isRemoved ? value.remove.filter((item) => item !== right) : [...value.remove, right]
    });
  };

  return (
    <StyledContent>
      <StyledColumns>
        {mode === 'add' && (
          <AddAvailList
            onItemAdd={handleAddRight}
            onItemsAdd={handleAddMultipleRights}
            type={tab}
            selectedRightsList={displayedRights}
            rightsOptions={rightsOptions}
            scrollToElementName={scrollToElementName}
            hideSelectAll
            onlyRights
          />
        )}
        <IncrementalSelectedAvailList
          originalRights={originalRights}
          addRights={value.add}
          removeRights={value.remove}
          type={tab}
          rightsOptions={rightsOptions}
          onItemToggle={handleToggleRight}
          disableTakedown={mode !== 'remove'}
        />
      </StyledColumns>
    </StyledContent>
  );
};

export default IncrementalLaunchRightsSelectionSection;

const StyledColumns = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: 1fr;
  }
`;

export const HEIGHT = 536;

const StyledContent = styled.div`
  flex: 1;
  min-height: ${HEIGHT}px;
  max-height: ${HEIGHT}px;
  display: flex;
  flex-direction: column;
`;
